import {
  campaignApiGet,
  campaignApiGetBlobDetail,
  campaignApiPost,
} from '@/lib/api/campaign-api-client';
import type { CampaignApiQueryValue } from '@/lib/api/campaign-api-client';
import type {
  AnalyticsEnvelope,
  CampaignEventPage,
  CampaignLinkReport,
  CampaignReport,
  CreateReportExportRequest,
  DeliverabilityReport,
  EventType,
  ReportExportJob,
  ReportOverview,
} from '../types/analytics-api.types';
import { assertAnalyticsRange, unwrapAnalytics } from '../utils/analytics-errors';

const definedQuery = (
  query: Record<string, string | number | undefined>
): Record<string, CampaignApiQueryValue> => {
  const out: Record<string, CampaignApiQueryValue> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    out[key] = value;
  }
  return out;
};

const rangeParams = (from?: string, to?: string) => {
  assertAnalyticsRange(from, to);
  return { from, to };
};

export type OverviewQuery = {
  from?: string;
  to?: string;
  interval?: 'day' | 'week';
};

export const getReportOverview = async (query: OverviewQuery = {}): Promise<ReportOverview> => {
  const response = await campaignApiGet<AnalyticsEnvelope<ReportOverview>>(
    '/v1/reports/overview',
    definedQuery({
      ...rangeParams(query.from, query.to),
      interval: query.interval,
    })
  );
  return unwrapAnalytics(response, 'Failed to load the reports overview');
};

export type DeliverabilityQuery = {
  from?: string;
  to?: string;
  groupBy?: 'domain' | 'day';
  campaignId?: string | null;
};

export const getDeliverability = async (
  query: DeliverabilityQuery = {}
): Promise<DeliverabilityReport> => {
  const response = await campaignApiGet<AnalyticsEnvelope<DeliverabilityReport>>(
    '/v1/reports/deliverability',
    definedQuery({
      ...rangeParams(query.from, query.to),
      groupBy: query.groupBy,
      campaignId: query.campaignId || undefined,
    })
  );
  return unwrapAnalytics(response, 'Failed to load deliverability');
};

export const getCampaignReport = async (
  campaignId: string,
  query: { sendJobId?: string } = {}
): Promise<CampaignReport> => {
  const response = await campaignApiGet<AnalyticsEnvelope<CampaignReport>>(
    `/v1/campaigns/${encodeURIComponent(campaignId)}/report`,
    definedQuery({ sendJobId: query.sendJobId })
  );
  return unwrapAnalytics(response, 'Failed to load the campaign report');
};

export type CampaignEventsQuery = {
  limit?: number;
  cursor?: string;
  type?: EventType;
  address?: string;
  contactId?: string;
  from?: string;
  to?: string;
};

export const getCampaignEvents = async (
  campaignId: string,
  query: CampaignEventsQuery = {}
): Promise<CampaignEventPage> => {
  const response = await campaignApiGet<AnalyticsEnvelope<CampaignEventPage>>(
    `/v1/campaigns/${encodeURIComponent(campaignId)}/events`,
    definedQuery({
      limit: query.limit,
      cursor: query.cursor,
      type: query.type,
      address: query.address?.trim() || undefined,
      contactId: query.contactId,
      ...rangeParams(query.from, query.to),
    })
  );
  return unwrapAnalytics(response, 'Failed to load recipient events');
};

export type CampaignLinksQuery = {
  deviceClass?: 'desktop' | 'mobile';
  from?: string;
  to?: string;
};

export const getCampaignLinks = async (
  campaignId: string,
  query: CampaignLinksQuery = {}
): Promise<CampaignLinkReport> => {
  const response = await campaignApiGet<AnalyticsEnvelope<CampaignLinkReport>>(
    `/v1/campaigns/${encodeURIComponent(campaignId)}/links`,
    definedQuery({
      deviceClass: query.deviceClass,
      ...rangeParams(query.from, query.to),
    })
  );
  return unwrapAnalytics(response, 'Failed to load the link report');
};

export const createReportExport = async (
  body: CreateReportExportRequest,
  idempotencyKey: string
): Promise<ReportExportJob> => {
  assertAnalyticsRange(body.from, body.to);
  const response = await campaignApiPost<AnalyticsEnvelope<ReportExportJob>>(
    '/v1/reports/exports',
    body,
    { 'Idempotency-Key': idempotencyKey }
  );
  return unwrapAnalytics(response, 'Failed to start the export');
};

export const getReportExport = async (itemId: string): Promise<ReportExportJob> => {
  const response = await campaignApiGet<AnalyticsEnvelope<ReportExportJob>>(
    `/v1/reports/exports/${encodeURIComponent(itemId)}`
  );
  return unwrapAnalytics(response, 'Failed to load the export');
};

const exportFileName = (contentDisposition: string | null): string => {
  const match = /filename="?([^";]+)"?/.exec(contentDisposition ?? '');
  return match?.[1] || 'export.csv';
};

export const downloadReportExport = async (
  itemId: string
): Promise<{ blob: Blob; fileName: string }> => {
  const detail = await campaignApiGetBlobDetail(
    `/v1/reports/exports/${encodeURIComponent(itemId)}/download`
  );
  return { blob: detail.blob, fileName: exportFileName(detail.contentDisposition) };
};
